import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

interface SEOConfig {
  title?: string;
  description?: string;
  keywords?: string;
  type?: string;
  image?: string;
  url?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SEOService {
  private readonly defaultConfig: SEOConfig = {
    title: 'Latest AI News & Summaries | Summerry',
    description: 'Stay updated with the latest AI news and summaries, curated by AI for relevance and clarity.',
    keywords: 'AI news, artificial intelligence, tech news, news summaries, machine learning',
    type: 'website',
    image: '/assets/og/black-og-image-1x.png',
  };

  constructor(
    private meta: Meta,
    private title: Title,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Update SEO meta tags for a generic page (e.g., list pages)
   */
  setGenericPageTags(config: SEOConfig): void {
    this.updateTags(config);
  }

  /**
   * Update SEO meta tags based on the provided configuration
   */
  updateTags(config: SEOConfig): void {
    // Merge default config with provided config
    const mergedConfig: SEOConfig = { ...this.defaultConfig, ...config };

    // Basic tags
    this.title.setTitle(mergedConfig.title || this.defaultConfig.title || '');

    this.meta.updateTag({
      name: 'description',
      content: mergedConfig.description || this.defaultConfig.description || '',
    });
    this.meta.updateTag({ name: 'keywords', content: mergedConfig.keywords || this.defaultConfig.keywords || '' });

    // Set canonical URL - critically important for SEO
    const url = this.generateCanonicalUrl(mergedConfig.url);

    // Now that we've set canonical URL, continue with other meta tags

    // Open Graph tags
    this.meta.updateTag({ property: 'og:title', content: mergedConfig.title || this.defaultConfig.title || '' });
    this.meta.updateTag({
      property: 'og:description',
      content: mergedConfig.description || this.defaultConfig.description || '',
    });
    this.meta.updateTag({ property: 'og:type', content: mergedConfig.type || this.defaultConfig.type || 'website' });
    this.meta.updateTag({ property: 'og:url', content: url });

    if (mergedConfig.image) {
      const imageUrl = mergedConfig.image.startsWith('http')
        ? mergedConfig.image
        : `${environment.canonicalOrigin}${mergedConfig.image}`;

      this.meta.updateTag({ property: 'og:image', content: imageUrl });
      this.meta.updateTag({ property: 'og:image:width', content: '1200' });
      this.meta.updateTag({ property: 'og:image:height', content: '630' });
    }

    // Twitter Card tags
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: mergedConfig.title || this.defaultConfig.title || '' });
    this.meta.updateTag({
      name: 'twitter:description',
      content: mergedConfig.description || this.defaultConfig.description || '',
    });

    if (mergedConfig.image) {
      const imageUrl = mergedConfig.image.startsWith('http')
        ? mergedConfig.image
        : `${environment.canonicalOrigin}${mergedConfig.image}`;

      this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
    }

    // Article specific tags
    if (mergedConfig.type === 'article') {
      if (mergedConfig.publishedTime) {
        this.meta.updateTag({ property: 'article:published_time', content: mergedConfig.publishedTime });
      }

      if (mergedConfig.modifiedTime) {
        this.meta.updateTag({ property: 'article:modified_time', content: mergedConfig.modifiedTime });
      }

      if (mergedConfig.author) {
        this.meta.updateTag({ property: 'article:author', content: mergedConfig.author });
      }
    }
  }

  /**
   * Generate a canonical URL for the current page
   * This is CRITICAL for SEO to prevent duplicate content issues
   *
   * @param customUrl Optional custom URL to use instead of the current route
   * @returns The clean canonical URL without query parameters or fragments
   */
  private generateCanonicalUrl(customUrl?: string): string {
    // Start with either the custom URL or build from current location
    let url = '';

    if (customUrl) {
      // If a custom URL is provided, use it
      url = customUrl.startsWith('http') ? customUrl : `${environment.canonicalOrigin}${customUrl}`;
    } else {
      // Otherwise use the current route
      const path = this.router.url.split('?')[0].split('#')[0]; // Remove query params and fragments
      url = `${environment.canonicalOrigin}${path}`;
    }

    // Set the canonical URL
    this.setCanonicalUrl(url);

    return url;
  }

  /**
   * Explicitly set the canonical URL for the current page
   * This is a public method that can be directly called from components
   *
   * @param url The full canonical URL to set
   */
  public setCanonicalUrl(url: string): void {
    // Ensure URL has no query parameters or hash fragments
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Set or update the canonical tag
    this.updateLinkTag({ rel: 'canonical', href: cleanUrl });

    // Also set default hreflang (English) - important for international SEO
    this.setHreflang(cleanUrl, 'en');
  }

  /**
   * Set hreflang tags for multi-language support
   * This is important for international SEO
   *
   * @param url The canonical URL
   * @param lang The language code (e.g., 'en', 'es', etc.)
   */
  public setHreflang(url: string, lang: string): void {
    // Set hreflang for the specified language
    this.updateLinkTag({ rel: 'alternate', hreflang: lang, href: url });

    // Also set x-default for language negotiation
    this.updateLinkTag({ rel: 'alternate', hreflang: 'x-default', href: url });
  }

  /**
   * Update or create a link tag in the document head
   */
  private updateLinkTag(attributes: { [key: string]: string }): void {
    let selector = '';
    if (attributes['rel'] === 'canonical') {
      selector = 'link[rel="canonical"]';
    } else if (attributes['rel'] === 'alternate' && attributes['hreflang']) {
      selector = `link[rel="alternate"][hreflang="${attributes['hreflang']}"]`;
    } else {
      // Fallback for other link types or if essential attributes are missing
      // This part may need adjustment if other types of link tags are managed by this function
      // and require a different update strategy. For now, we focus on canonical and hreflang.
      console.warn('updateLinkTag called with unhandled attributes combination:', attributes);
      // Construct a selector based on 'rel' if available, otherwise, it might not find specific tags to update.
      if (attributes['rel']) {
        selector = `link[rel="${attributes['rel']}"]`;
      } else {
        console.error('updateLinkTag called without "rel" attribute.');
        return; // Cannot proceed without 'rel'
      }
    }

    const linkElement = this.document.head.querySelector(selector);

    if (linkElement) {
      // Update existing tag
      Object.keys(attributes).forEach(key => {
        linkElement.setAttribute(key, attributes[key]);
      });
    } else {
      // Create new tag
      const link = this.document.createElement('link');
      Object.keys(attributes).forEach(key => {
        link.setAttribute(key, attributes[key]);
      });
      this.document.head.appendChild(link);
    }
  }
}
